import { Button, cn, DropdownMenu, Icon, IconButton, Popover, Tooltip } from '@magicschool/spellbook';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useTTS } from '@/contexts/TTSProvider';
import { prefersSingleTapPlayback } from '@/contexts/TTSProvider/utils/prefersSingleTapPlayback';
import type { MenuItem } from '@/features/chat/types';
import type { Voice } from '@/features/tts/types';
import { analyticsTrack } from '@/util/amplitude/analyticsClient';

type ReadAloudProps = {
  /**
   * The text to be read aloud.
   */
  text: string;

  /**
   * Voice options for the TTS playback.
   */
  voice?: Voice;

  /**
   * Optional prop to disable the button.
   * @default false
   */
  disabled?: boolean;

  /**
   * Optional label for the button.
   * @default 'Read Aloud'
   */
  label?: string;

  /**
   * Optional array of speed options for the TTS playback rate.
   * @default [0.75, 1, 1.25, 1.5, 2]
   */
  speedOptions?: number[];
  toolId: string;
  /**
   * Optional overrides for the trigger IconButton styling.
   */
  triggerVariant?: React.ComponentProps<typeof IconButton>['variant'];
  triggerSize?: React.ComponentProps<typeof IconButton>['size'];
  triggerColor?: React.ComponentProps<typeof IconButton>['color'];
};

export const ReadAloud = ({
  text,
  disabled,
  label,
  voice,
  speedOptions = [0.75, 1, 1.25, 1.5, 2],
  toolId,
  triggerVariant = 'ghost',
  triggerSize = 'md',
  triggerColor,
}: ReadAloudProps) => {
  const tts = useTTS();
  const intl = useIntl();
  const isCoarsePointer = prefersSingleTapPlayback();

  const [isOpen, setIsOpen] = useState(false);
  const [speedMenuOpen, setSpeedMenuOpen] = useState(false);

  const isPlayingThisText = tts.isPlayingText(text);
  const isPreparing = tts.isLoadedText(text) && !tts.isReady;
  const playbackRate = tts.rate;
  const progress = tts.progress;
  const success = !tts.error;
  const triggerColorToUse = triggerColor ?? (isPlayingThisText ? 'primary' : 'neutral');

  const getAnimationDuration = useCallback(() => {
    return parseInt(getComputedStyle(document.documentElement).getPropertyValue('--read-aloud-animation-duration') || '200');
  }, []);

  // Toggle play/pause
  const handlePlayPause = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (isPlayingThisText) {
      tts.pause();
      analyticsTrack('GenerationForm:TTSListen', {
        method: 'audio-stream',
        success,
        progress,
        toolId,
        action: 'pause',
        version: 'version2',
      });
    } else {
      if (tts.isPaused && tts.isLoadedText(text)) {
        tts.resume();
        analyticsTrack('GenerationForm:TTSListen', {
          method: 'audio-stream',
          success,
          progress,
          toolId,
          action: 'resume',
          version: 'version2',
        });
      } else {
        tts.play(text, { voice });
        analyticsTrack('GenerationForm:TTSListen', {
          method: 'audio-stream',
          success,
          progress,
          toolId,
          action: 'play',
          version: 'version2',
        });
      }
    }
  };

  // Handle reset or abort
  const handleResetOrAbort = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (isPreparing) {
      tts.abort();
    } else {
      tts.reset();
    }
  };

  const handleSpeedChange = (speed: number) => {
    tts.setRate(speed);

    setTimeout(() => {
      setSpeedMenuOpen(false);
    }, getAnimationDuration() / 2);
  };

  const speedMenuItems: MenuItem[] = speedOptions.map((speed) => ({
    id: `speed-${speed}`,
    label: `${speed}x`,
    onClick: () => handleSpeedChange(speed),
    selected: playbackRate === speed,
  }));

  const handleScroll = useCallback(() => {
    if (isOpen) {
      setIsOpen(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      window.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen, handleScroll]);

  const getPlayPauseIcon = () => {
    if (isPreparing) return 'loader-2';
    if (isPlayingThisText) return 'pause';
    return 'play';
  };

  const getPlayPauseLabel = () => {
    let id: string;
    if (isPreparing) id = 'chat.action.tts.preparing';
    else if (isPlayingThisText) id = 'chat.action.tts.pause';
    else id = 'chat.action.tts.play';
    return intl.formatMessage({ id });
  };

  const getTriggerIcon = () => {
    if (isPreparing) return 'loader-2';
    if (isPlayingThisText) return 'pause';
    return 'volume-2';
  };

  // Reference for the popover content
  const popoverRef = useRef<HTMLDivElement>(null);

  if (isCoarsePointer) {
    return (
      <IconButton
        name={getTriggerIcon()}
        color={triggerColorToUse}
        variant={isPlayingThisText ? 'outlined' : triggerVariant}
        size={triggerSize}
        label={label}
        disabled={disabled || isPreparing}
        data-testid="message-action-read-aloud"
        isLoading={isPreparing}
        onClick={handlePlayPause}
      />
    );
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <Popover.Tooltip side="top">
        <Popover.Trigger>
          <IconButton
            name="volume-2"
            color={triggerColorToUse}
            variant={triggerVariant}
            size={triggerSize}
            label={label}
            disabled={disabled}
            data-testid="message-action-read-aloud"
          />
        </Popover.Trigger>
        <Popover.TooltipContent>{intl.formatMessage({ id: 'chat.action.read-aloud' })}</Popover.TooltipContent>
      </Popover.Tooltip>

      <Popover.Content side="bottom" onOpenAutoFocus={(e) => e.preventDefault()} ref={popoverRef}>
        <div className="flex items-center gap-1 p-2">
          {/* Play/Pause button */}
          <Tooltip align="center" className="z-1000">
            <Tooltip.Content>{getPlayPauseLabel()}</Tooltip.Content>
            <Tooltip.Trigger>
              <IconButton
                name={getPlayPauseIcon()}
                onClick={handlePlayPause}
                className={cn('transition-all duration-150', isPreparing && '[&_svg]:animate-spin', !isPreparing && '[&_svg]:fill-current')}
                color="neutral"
                variant="ghost"
                shape="circle"
                disabled={isPreparing}
                label={getPlayPauseLabel()}
                data-testid="read-aloud-play-pause"
              />
            </Tooltip.Trigger>
          </Tooltip>

          {/* Reset/Abort button */}
          <Tooltip align="center" className="z-1000">
            <Tooltip.Content>
              <FormattedMessage id={isPreparing ? 'chat.action.tts.stop-loading' : 'chat.action.tts.restart'} />
            </Tooltip.Content>
            <Tooltip.Trigger>
              <IconButton
                name={isPreparing ? 'square' : 'rotate-ccw'}
                onClick={handleResetOrAbort}
                color={isPreparing ? 'error' : 'neutral'}
                variant="ghost"
                shape="circle"
                disabled={!isPreparing && progress === 0}
                label={label}
                data-testid="read-aloud-restart"
              />
            </Tooltip.Trigger>
          </Tooltip>

          {/* Speed selector with FloatingContextMenu */}
          <Tooltip align="center" className="z-1000">
            <Tooltip.Content>
              <FormattedMessage id="chat.action.tts.playback-speed" />
            </Tooltip.Content>
            <Tooltip.Trigger>
              <div>
                <DropdownMenu>
                  <DropdownMenu.Trigger disabled={disabled}>
                    <Button
                      color="neutral"
                      variant="ghost"
                      aria-label={intl.formatMessage({ id: 'chat.action.tts.playback-speed' })}
                      aria-haspopup="true"
                      aria-expanded={speedMenuOpen}
                      data-testid="read-aloud-speed-button"
                    >
                      {playbackRate}x
                      <Icon name="chevron-down" size="sm" className="ml-1" />
                    </Button>
                  </DropdownMenu.Trigger>
                  <DropdownMenu.Content>
                    {speedMenuItems.map((item) => (
                      <DropdownMenu.Item key={item.id} onClick={item.onClick} data-testid={`read-aloud-speed-${item.label}`}>
                        <DropdownMenu.ItemLabel>{item.label}</DropdownMenu.ItemLabel>
                      </DropdownMenu.Item>
                    ))}
                  </DropdownMenu.Content>
                </DropdownMenu>
              </div>
            </Tooltip.Trigger>
          </Tooltip>
        </div>
      </Popover.Content>
    </Popover>
  );
};
